import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Typography } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../../redux";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import CustomButton from "../../../../../theming/CustomButton";
import styles from "../../../../../../styles/custom-styles.module.scss";
import { getCourseTags } from "../../../../../../redux/courseTags/actions";
import CustomTextField from "../../../../../forms/CustomTextField";
/**
 * Interface representing a tags item.
 */
interface TagsItem {
  id: string;
  name: string;
}

/**
 * `Tags` component manages assignment and removal of tags items
 * from an event/course using Formik context. It fetches available tags from Redux,
 * and synchronizes selected items with Formik's `Tags` field.
 *
 * @returns JSX.Element
 */
export const Tags: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, setFieldValue } = useFormikContext<any>();
  const dispatch = useDispatch();
  // Assigned tags from form values
  const assignedTags: TagsItem[] = values.tags ?? [];
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 5;
  // Accessibility settings from Redux
  const accessibility = useSelector((state: AppState) => state.accessibility);

  // Fetch training tags on mount
  useEffect(() => {
    dispatch(getCourseTags());
  }, []);

  // Available tags from Redux state
  const availableTags = useSelector((state: AppState) => state.courseTags.courseTags);

  /**
   * Adds a tags item to the assigned list if not already present.
   */
  const addContent = (item: TagsItem) => {
    if (!assignedTags.find((i) => i.id === item.id)) {
      setFieldValue("tags", [...assignedTags, item]);
    }
  };

  /**
   * Removes a tags item from the assigned list.
   */
  const removeContent = (item: TagsItem) => {
    setFieldValue(
      "tags",
      assignedTags.filter((i) => i.id !== item.id)
    );
  };

  /**
   * Returns a list of tags items that are available for assignment
   * (i.e., not already assigned).
   */
  const filterAvailable = (): TagsItem[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return availableTags.filter((c: any) => !assignedTags.some((a) => a.id === c.id));
  };

  const filteredAvailable = filterAvailable().filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredAvailable.length / itemsPerPage);
  const paginatedAvailable = filteredAvailable.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Box className="tags">
      <h3>Zugewiesene Stichwörter</h3>
      <Box style={{ marginBottom: "1rem" }}>
        {assignedTags.length === 0 ? (
          <Typography variant="body2">Noch keine Inhalte zugewiesen</Typography>
        ) : (
          assignedTags.map((item) => (
            <Grid
              key={item.id}
              container
              alignItems="center"
              spacing={1}
              style={{ marginBottom: "0.5rem" }}
            >
              <Grid item xs>
                <Typography
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {item.name}
                </Typography>
              </Grid>
              <Grid item>
                <CustomButton
                  onClick={() => removeContent(item)}
                  onMouseEnter={() => {
                    setIsHovered(true);
                  }}
                  onMouseLeave={() => {
                    setIsHovered(false);
                  }}
                  className="speakers-list-item-addButton"
                  customColor={accessibility.monoMode ? "#000" : styles["red-accent-1"]}
                  hoverColor={accessibility.monoMode ? "#fff" : styles["red-accent-2"]}
                  textColor={accessibility.monoMode ? "#fff" : styles.red}
                >
                  <RemoveIcon
                    color={
                      isHovered
                        ? accessibility.monoMode
                          ? "primary"
                          : "error"
                        : "inherit"
                    }
                  />
                </CustomButton>
              </Grid>
            </Grid>
          ))
        )}
      </Box>

      <h3>Stichwörter hinzufügen</h3>
      <Box mb={2}>
        <CustomTextField
          fullWidth
          name="vnr"
          id="search_tags"
          label="Inhalt suchen.."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </Box>

      <Box>
        {filteredAvailable.length === 0 ? (
          <Typography variant="body2">Keine weiteren Inhalte verfügbar</Typography>
        ) : (
          <>
            {paginatedAvailable.map((item) => (
              <Grid key={item.id} container alignItems="center" spacing={1}>
                <Grid item xs>
                  <Typography
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {item.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => addContent(item)}>
                    <AddIcon className={styles["icon-primary"]} />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2} style={{ gap: "1rem" }}>
                <CustomButton
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  customColor={accessibility.monoMode ? "#000" : styles.secondary}
                >
                  Zurück
                </CustomButton>
                <Typography variant="body2" style={{ alignSelf: "center" }}>
                  Seite {currentPage} von {totalPages}
                </Typography>
                <CustomButton
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  customColor={accessibility.monoMode ? "#000" : styles.secondary}
                >
                  Weiter
                </CustomButton>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
